import React from "react";

const ONLINE_MS = 5 * 60 * 1000; // 5 min
const AWAY_MS = 30 * 60 * 1000; // 30 min

function formatRelative(diffMs) {
    if (diffMs < 60 * 1000) return "moments ago";
    const min = Math.floor(diffMs / 60000);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.floor(hr / 24);
    if (d < 30) return `${d}d ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo}mo ago`;
    return `${Math.floor(mo / 12)}y ago`;
}

/**
 * Presence indicator for the admin students table — a small coloured dot
 * plus a relative "last seen" label.
 *
 * States:
 * - **Online** (green): last_seen_at within 5 min → the student is in the
 *   app right now.
 * - **Away** (amber): within 30 min → still probably around.
 * - **Offline** (grey): older than 30 min, or never seen (brand-new account).
 *
 * The dot has `role="status"` and an `aria-label` so screen-readers get a
 * proper announcement. Keep the component dumb — no timers, it renders
 * whatever the parent fetched. Parents that want a live ticking counter
 * should refresh their data on an interval.
 */
export default function PresenceIndicator({ lastSeenAt, compact = false }) {
    const now = Date.now();
    const seen = lastSeenAt ? new Date(lastSeenAt).getTime() : null;
    const diff = seen ? now - seen : null;

    let state, color, label;
    if (diff == null) {
        state = "offline";
        color = "bg-muted-foreground/30 ring-muted-foreground/20";
        label = "Never signed in";
    } else if (diff < ONLINE_MS) {
        state = "online";
        color = "bg-emerald-500 ring-emerald-500/30 animate-pulse";
        label = "Online now";
    } else if (diff < AWAY_MS) {
        state = "away";
        color = "bg-amber-500 ring-amber-500/30";
        label = `Away · ${formatRelative(diff)}`;
    } else {
        state = "offline";
        color = "bg-muted-foreground/40 ring-muted-foreground/20";
        label = formatRelative(diff);
    }

    if (compact) {
        return (
            <span
                className="relative inline-flex items-center"
                title={label}
                data-testid={`presence-${state}`}
            >
                <span
                    role="status"
                    aria-label={label}
                    className={`h-2.5 w-2.5 rounded-full ring-2 ${color}`}
                />
            </span>
        );
    }

    return (
        <span
            className="inline-flex items-center gap-1.5"
            title={label}
            data-testid={`presence-${state}`}
        >
            <span
                role="status"
                aria-label={label}
                className={`h-2.5 w-2.5 rounded-full ring-2 ${color}`}
            />
            <span
                className={`text-[11px] tabular-nums ${
                    state === "online"
                        ? "text-emerald-700 font-semibold"
                        : state === "away"
                            ? "text-amber-700"
                            : "text-muted-foreground"
                }`}
            >
                {state === "online"
                    ? "Online now"
                    : state === "offline" && diff == null
                        ? "Never signed in"
                        : formatRelative(diff)}
            </span>
        </span>
    );
}
